import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Field, Label } from '@shared/ui/Input';
import { useComposeClaimRedirectMutation } from '@shared/api';
import { CounterpartyPicker } from './CounterpartyPicker';
import { ContractPicker } from './ContractPicker';

/**
 * Stage E ADR-0001: модалка перевыставления входящей претензии на
 * субподрядчика. Полный async DOCX-pipeline.
 *
 * Флоу:
 *   1. Юрист выбирает субподрядчика (CounterpartyPicker с typeahead).
 *   2. Выбирает договор субподряда (ContractPicker, фильтрованный по
 *      counterparty_id выбранного субподрядчика).
 *   3. Жмёт «Сгенерировать DOCX» — POST /compose-redirect ставит
 *      Job::ComposeClaimRedirect в очередь, сразу возвращается
 *      `{status:'queued'}`. Модалка закрывается через 1.5 сек с
 *      сообщением «Запустили в фоне, готовый файл появится в блоке
 *      «Черновики DOCX» в карточке».
 *   4. Worker через 30-60с пришлёт WS-event `claim_response_composed`,
 *      rtkBridge инвалидирует ComposedResponses → блок в ClaimCard сам
 *      обновится и покажет новый DOCX.
 *
 * Юристу больше не нужно сидеть с открытой модалкой — может закрыть
 * её сразу и заниматься другими делами.
 */

const Step = styled.section`
  margin-top: ${({ theme }) => theme.spacing(4)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Lede = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ErrorBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger + '14'};
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.45;
`;

const SuccessBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.success + '14'};
  border-left: 3px solid ${({ theme }) => theme.colors.status.success};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  line-height: 1.55;
`;

interface Props {
  open: boolean;
  claimId: string;
  /** Текущая привязка договора генподряда. Без неё перевыставить нельзя —
   *  это валидируется и на бэке, но кнопка модалки сразу подсветит проблему. */
  mainContractLinked: boolean;
  onClose: () => void;
  /** Колбэк после успешной постановки job в очередь. Оставлен для
   *  совместимости с сигнатурой родителя, в новом async-flow не нужен. */
  onSaved?: () => void;
}

function extractApiError(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { error?: { message?: string } } }).data;
    if (data?.error?.message) return data.error.message;
  }
  return 'Не удалось выполнить запрос — попробуйте ещё раз.';
}

export function RedirectClaimModal({
  open,
  claimId,
  mainContractLinked,
  onClose,
  onSaved,
}: Props) {
  const [subcontractorId, setSubcontractorId] = useState<string | null>(null);
  const [subcontractorName, setSubcontractorName] = useState<string>('');
  const [subcontractId, setSubcontractId] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [composeRedirect, { isLoading: isQueuing }] =
    useComposeClaimRedirectMutation();

  useEffect(() => {
    if (!open) {
      setSubcontractorId(null);
      setSubcontractorName('');
      setSubcontractId(null);
      setQueued(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    setSubcontractId(null);
    setQueued(false);
  }, [subcontractorId]);

  // Автозакрытие модалки через 1.5 сек после постановки в очередь —
  // юристу не нужно сидеть и ждать, всё видно в карточке.
  useEffect(() => {
    if (!queued) return;
    const t = window.setTimeout(() => {
      onClose();
    }, 1500);
    return () => window.clearTimeout(t);
  }, [queued, onClose]);

  const canSubmit = useMemo(
    () =>
      Boolean(subcontractorId && subcontractId) &&
      !isQueuing &&
      mainContractLinked &&
      !queued,
    [subcontractorId, subcontractId, isQueuing, mainContractLinked, queued],
  );

  async function onSubmit() {
    if (!subcontractorId || !subcontractId) return;
    setError(null);
    try {
      await composeRedirect({
        claimId,
        subcontractorId,
        subcontractId,
      }).unwrap();
      setQueued(true);
      onSaved?.();
    } catch (e) {
      setError(extractApiError(e));
    }
  }

  return (
    <Modal
      open={open}
      title="Перевыставить претензию на субподрядчика"
      onClose={onClose}
    >
      <Lede>
        К нам поступила претензия от заказчика, но фактическим виновником
        является субподрядчик. Сейчас сгенерируем новую исходящую претензию
        к субподрядчику в порядке регресса (ст. 1081 / 393 ГК РФ). Фирменный
        бланк (шапка, реквизиты, подпись) возьмётся из вашего шаблона в БЗ
        («Шаблоны исходящих документов» → тип «Шаблон исходящей претензии»);
        тело адаптируется под факты этой претензии.
      </Lede>

      {!mainContractLinked && (
        <ErrorBox>
          У претензии не связан договор генподряда. Свяжите его в карточке
          претензии — без него перевыставить нельзя.
        </ErrorBox>
      )}

      <Step>
        <h4>1. Субподрядчик</h4>
        <Field>
          <Label>Контрагент-субподрядчик</Label>
          <CounterpartyPicker
            counterpartyId={subcontractorId}
            counterpartyName={subcontractorName}
            onChange={({ id, name }) => {
              setSubcontractorId(id);
              setSubcontractorName(name);
            }}
          />
        </Field>
      </Step>

      {subcontractorId && (
        <Step>
          <h4>2. Договор субподряда</h4>
          <Field>
            <Label>Выберите договор с этим субподрядчиком</Label>
            <ContractPicker
              counterpartyId={subcontractorId}
              value={subcontractId}
              onSelect={(cid) => setSubcontractId(cid)}
              disabled={isQueuing || queued}
            />
          </Field>
        </Step>
      )}

      {queued ? (
        <Step>
          <SuccessBox role="status">
            <strong>Запустили генерацию в фоне.</strong>
            <br />
            DOCX появится в карточке претензии в блоке «Черновики DOCX» через
            30-60 секунд. Можно закрыть это окно и заниматься другими делами —
            файл обновится сам, когда будет готов.
          </SuccessBox>
        </Step>
      ) : (
        subcontractId && (
          <ActionsBar>
            <Button type="button" onClick={onSubmit} disabled={!canSubmit}>
              {isQueuing ? 'Запускаю…' : 'Сгенерировать DOCX'}
            </Button>
          </ActionsBar>
        )
      )}

      {error && <ErrorBox role="alert">{error}</ErrorBox>}
    </Modal>
  );
}
